import React, { useMemo } from 'react';
import { echartsContent, getPercentageInfo } from '@/pages/opinion/utils';
import starBlue from '@/pages/opinion/assets/svg/starblue.svg';
import {
  formatter,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { ratingColors } from '@/pages/opinion/constants';
import {
  formatTimeXAxisText,
  setIntervalValue,
} from '@/pages/opinion/utils/chart';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import './GameStoreMobileChart.scss';
import moment from 'moment';
import { goStatsDetailAction } from '@/pages/opinion/pages/stats/Detail';
import { useHistory } from 'umi';

export function GameStoreMobileChart({
  data = {},
  filter,
  formatMessage,
  channel,
  height = 235,
  loadingHeight = 235,
  isMin = false,
  hideSelected = false,
  hideLifetime = false,
  loading = false,
  columns: columnsProps,
  leftTitle,
  rightTitle,
  lifetimeTitle,
  selectedTitle,
  sumTitle,
}: any) {
  const { lifetime, selected, total, time, star1, star2, star3, star4, star5 } =
    useMemo(() => {
      return (
        data || {
          lifetime: [],
          selected: [],
          total: [],
          time: [],
          star1: [],
          star2: [],
          star3: [],
          star4: [],
          star5: [],
        }
      );
    }, [data]);

  const nodata = useMemo(() => {
    try {
      let lifetimeNoData =
        lifetime?.length === 0 ||
        lifetime?.every((value: number) => value === 0);
      let selectedNoData =
        selected?.length === 0 ||
        selected?.every((value: number) => value === 0);
      let totalNoData =
        total?.length === 0 || total?.every((value: number) => value === 0);
      if (hideSelected) return lifetimeNoData && totalNoData;
      if (hideLifetime) return totalNoData && selectedNoData;
      return lifetimeNoData && selectedNoData && totalNoData;
    } catch (error) {
      return false;
    }
  }, [lifetime, selected, total]);

  function filterFunc(item: { data_key?: string }) {
    if (!hideLifetime && !hideSelected) return true;
    else if (hideLifetime) return item.data_key !== 'lifetime';
    else if (hideSelected) return item.data_key !== 'selected';
  }

  let option: any = {
    tooltip: {
      borderWidth: 0,
      padding: 0,
      confine: true,
      trigger: 'axis',
      extraCssText: 'z-index:8',
      formatter: (params: any) => {
        const newparams = params.filter?.(
          (v: any) => v.seriesType === 'bar' && v.seriesName !== 'total总数',
        ); // 过滤掉'total总数'的柱子
        const lineparams = params.filter?.((v: any) => v.seriesType === 'line');
        // 时间
        let dataTime = '';
        if (newparams.length > 0) {
          dataTime = listTimeTitleFunc(
            newparams[0]?.axisValue,
            filter?.date_type || 'daily',
          );
        }

        // 计算总人数
        let totalReviews = 0;
        for (let i = 0; i < newparams.length; i++) {
          if (
            newparams[i].seriesName != '' &&
            newparams[i].value !== undefined &&
            newparams[i].value !== 0
          ) {
            totalReviews += newparams[i].value;
          }
        }

        const lineRecords: any[] = [];
        const barRecords: any[] = [];

        for (let i = 0; i < lineparams.length; i++) {
          const { marker, seriesName } = lineparams[i];
          lineRecords.push({
            type: `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;${seriesName}`,
            score: lineparams[i]?.value
              ? Number(lineparams[i]?.value).toFixed(1) === '0.0'
                ? '-'
                : Number(lineparams[i]?.value).toFixed(1)
              : '-',
          });
        }

        barRecords.push({
          type: formatMessage({ id: sumTitle || 'Sum of Reviews' }),
          score: totalReviews === 0 ? '-' : echartsContent(totalReviews),
          percentage: totalReviews === 0 ? '-' : '100%',
        });

        for (let i = 0; i < newparams.length; i++) {
          if (
            newparams[i].seriesName != '' &&
            newparams[i].value !== undefined &&
            newparams[i].value !== 0
          ) {
            const { marker } = newparams[i];

            barRecords.push({
              type: `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;<span class="tooltip-star tooltip-star-${newparams[i].seriesName[0]}"></span>`,
              score: echartsContent(newparams[i].value),
              percentage: getPercentageInfo(
                newparams[i].value,
                totalReviews,
                0,
              ),
            });
          }
        }

        return formatter({
          title: dataTime,
          tables: [
            {
              showHeader: false,
              records: lineRecords,
              columns: [
                {
                  key: 'type',
                },
                {
                  weight: 'bold',
                  align: 'right',
                  key: 'score',
                },
              ],
            },
            {
              records: barRecords,
              columns: columnsProps || [
                {
                  key: 'type',
                  header: formatMessage({ id: 'Rating' }),
                },
                {
                  weight: 'bold',
                  align: 'right',
                  key: 'score',
                  header: formatMessage({ id: 'Reviews' }),
                },
                {
                  weight: 'bold',
                  align: 'right',
                  key: 'percentage',
                  header: formatMessage({ id: 'Share-1' }),
                },
              ],
            },
          ],
        });
      },
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          shadowColor: '#BBBBBB',
          shadowBlur: 1,
        },
      },
    },
    legend: {
      data: [
        {
          data_key: 'selected',
          name: formatMessage({ id: lifetimeTitle || 'Lifetime User Score' }),
          itemStyle: {
            color: '#5086F3',
          },
          textStyle: { color: '#888' },
        },
        {
          data_key: 'lifetime',
          name: formatMessage({ id: selectedTitle || 'Selected User Score' }),
          itemStyle: {
            color: !hideLifetime && !hideSelected ? '#6A7597' : '#5086F3',
          },
          textStyle: { color: '#888' },
        },
        {
          name: '1星',
          itemStyle: {
            color: ratingColors[0],
          },
        },
        {
          name: '2星',
          itemStyle: {
            color: ratingColors[1],
          },
        },
        {
          name: '3星',
          itemStyle: {
            color: ratingColors[2],
          },
        },
        {
          name: '4星',
          itemStyle: {
            color: ratingColors[3],
          },
        },
        {
          name: '5星',
          itemStyle: {
            color: ratingColors[4],
          },
        },
      ],
      formatter: (params: any) => {
        if (!parseInt(params[0])) return params;
        params = parseInt(params[0]);
        let legendIcon = '';
        for (let index = 0; index < params; index++) {
          legendIcon += `{databrainImg|}`;
        }

        return legendIcon;
      },
      textStyle: {
        rich: {
          a: {
            color: '#888888',
            fontSize: 12,
          },
          databrainImg: {
            height: 10,
            width: 10, // 星星间有2px间距
            backgroundColor: {
              image: starBlue,
            },
          },
        },
      },
    },
    grid: {
      top: '20px',
      right: '0%',
      bottom: '15%',
      containLabel: true,
    },
    axisPointer: getAxisPointer((data) =>
      formatTimeXAxisText(data.value, filter.date_type),
    ),
    xAxis: {
      type: 'category',
      boundaryGap: ['20%', 0],
      data: time,
      axisLabel: {
        showMaxLabel: true,
        formatter: (value: string) =>
          formatTimeXAxisText(value, filter.date_type),
      },
    },
    yAxis: [
      {
        axisLabel: {
          formatter: (value: string) => {
            return echartsContent(value);
          },
        },
        min: 0,
        splitNumber: 5,
        type: 'value',
      },
      {
        type: 'value',
        nameTextStyle: {
          padding: [0, 35, 0, 0],
          align: 'center',
        },
        min: 0,
        max: 5,
        splitNumber: 5,
        axisLabel: {
          formatter: (value: string) => {
            return echartsContent(value);
          },
        },
      },
    ],
    series: [],
  };

  option.series = [
    {
      name: 'total总数',
      type: 'bar',
      barMaxWidth: 50, // 柱子宽度同实体柱子，否则会错位
      barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
      label: {
        normal: {
          show: lifetime?.length < 20 && !isMin,
          position: 'top', //  位置设为top
          formatter: (c: any) => echartsContent(c?.value),
          textStyle: { color: '#444' },
        },
      },
      itemStyle: {
        normal: {
          color: 'rgba(128, 128, 128, 0)', // 为透明
        },
      },
      data: total,
    },
    {
      name: '5星',
      type: 'bar',
      stack: 'time',
      barMaxWidth: 50,
      itemStyle: {
        color: ratingColors[4],
      },
      data: star5,
    },
    {
      name: '4星',
      type: 'bar',
      stack: 'time',
      barMaxWidth: 50,
      itemStyle: {
        color: ratingColors[3],
      },
      data: star4,
    },
    {
      name: '3星',
      type: 'bar',
      stack: 'time',
      barMaxWidth: 50,
      itemStyle: {
        color: ratingColors[2],
      },
      data: star3,
    },
    {
      name: '2星',
      type: 'bar',
      stack: 'time',
      barMaxWidth: 50,
      itemStyle: {
        color: ratingColors[1],
      },
      data: star2,
    },
    {
      name: '1星',
      type: 'bar',
      stack: 'time',
      barMaxWidth: 50,
      itemStyle: {
        color: ratingColors[0],
      },
      data: star1,
    },
    {
      name: formatMessage({ id: lifetimeTitle || 'Lifetime User Score' }),
      type: 'line',
      yAxisIndex: 1,
      itemStyle: {
        color: '#5086F3',
      },
      data_key: 'lifetime',
      data: lifetime?.map((val) => (val ? val : null)) || [],
    },
    {
      name: formatMessage({ id: selectedTitle || 'Selected User Score' }),
      type: 'line',
      yAxisIndex: 1,
      itemStyle: {
        color: !hideLifetime && !hideSelected ? '#6A7597' : '#5086F3',
      },
      data_key: 'selected',
      data: selected?.map((val) => (val ? val : null)) || [],
    },
  ].filter((item) => filterFunc(item));

  setIntervalValue(total, option.yAxis[0]);

  const history = useHistory();

  return (
    <EChartCommon
      options={option}
      height={height}
      loadingHeight={loadingHeight}
      loading={loading}
      empty={nodata}
      leftTitle={formatMessage({ id: leftTitle || 'Reviews-2' })}
      rightTitle={formatMessage({ id: rightTitle || 'User Score' })}
      eventsDict={{
        click: (params: any) => {
          let start_time = moment(params.name).format('YYYY-MM-DD');
          let end_time = moment(params.name).format('YYYY-MM-DD');

          if (filter.date_type === 'weekly') {
            end_time = moment(start_time).add(6, 'days').format('YYYY-MM-DD');
          } else if (filter.date_type === 'hourly') {
            start_time = moment(params.name).format('YYYY-MM-DD HH:mm:ss');
            end_time = moment(params.name)
              .endOf('h')
              .format('YYYY-MM-DD HH:mm:ss');
          }

          let {
            channel_comments,
            channel_social,
            channel_securitys,
            channel_news,
            channel: channelInfo,
            channel_type,
            ...otherFilter
          } = filter;
          console.log(channel);
          goStatsDetailAction({
            path: `/opinion/Feeds/Feeds?tab=gameStore`,
            params: {
              statsDetailType: 'feeds-gameStoreLine',
              ...otherFilter,
              start_time,
              end_time,
              channel: [channel],
              channel_type: 'comments',
            },
            history,
          });
        },
      }}
      className={'dbo-gamestore-mobile-chart'}
    />
  );
}
